import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { useLocale } from '@/hooks/use-locale';
import clsx from 'clsx';
import { CityDetailResults } from '@/types/marketplace';
import { CitiesGridModal } from '../cities-grid-modal';

interface CitiesDropdownButtonProps {
  selectedCity: CityDetailResults | null;
  onCitySelect: (city: CityDetailResults) => void;
  inputActive?: boolean;
  onAllCitiesSelect?: () => void;
}

const CitiesDropdownButton: React.FC<CitiesDropdownButtonProps> = ({
  selectedCity,
  onCitySelect,
  inputActive = false,
  onAllCitiesSelect,
}) => {
  const { currentLocale } = useLocale();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    function handleScroll(event: Event) {
      if (event.target === document || event.target === window) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        style={{
          background: inputActive ? 'rgba(108, 117, 125, 0.1)' : 'rgba(255, 255, 255, 0.2)',
        }}
        className={clsx(
          'flex items-center justify-center',
          'h-[56px] rounded-[16px] border-none',
          'px-3 py-4',
          'text-[16px] font-semibold',
          'hover:bg-bg-primaryLight/40 hover:rounded-lg',
          inputActive ? 'text-N900' : 'border-none text-white'
        )}
        onClick={() => setOpen((v) => !v)}
      >
        {selectedCity
          ? selectedCity.name_translations[currentLocale]
          : t('js.components.all_cities_dropdown.all_cities')}
        <IconSaxIcon
          name="arrow-down"
          size={24}
          strokeColor={inputActive ? '#000' : '#fff'}
          className="ml-2"
        />
      </button>

      <CitiesGridModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onCitySelect={(city) => {
          onCitySelect(city);
          setOpen(false);
        }}
        onAllCitiesClick={onAllCitiesSelect ? () => { onAllCitiesSelect(); setOpen(false); } : undefined}
        position="centered"
        dropdownRef={dropdownRef}
      />
    </div>
  );
};

export { CitiesDropdownButton };
